import React, {useCallback, useState, useEffect} from "react"
import moment from "moment"
import * as XLSX from "xlsx"
import {apiV2, platformAPI} from "../../../api/axiosConfig"
import {AlertState, StatusSnapshot} from "../hooks/useSirenAlertWS"
import {Card, InfoRow, STATUS_COLORS, statusLabel, deliveryMethodLabel, formatSkipReason, formatSeconds, SirenAlert} from "./AlertStatusShared"
import {UserName} from "../../../components/data/UserName"
import clsx from "clsx"

type RecipientExportRow = {
    id: string
    firstName: string
    lastName: string
    email: string
    personalEmail: string
    workMobileE164: string
    personalMobileE164: string
    homePhoneE164: string
    deliveryMethods: string[]
    currentCycle: number
    responseCycle: number | null
    responseMethod: string | null
    responseVia: string | null
    responseValue: string | null
    respondedAt: string | null
}

type AttemptExportRow = {
    id: string
    recipientID: string
    firstName: string
    lastName: string
    cycle: number
    deliveryMethod: string
    from: string
    to: string
    status: string
    skipReason: string
    createdAt: string
    sentAt: string | null
    failedAt: string | null
}

function formatResponseVia(via: string | null): string {
    if (!via) return "—"
    switch (via) {
        case "sms_reply":   return "SMS Reply"
        case "portal_link": return "Response Link"
    }
    return via
}

export function AlertInfoCard({
    alertID,
    snapshot,
    state,
    connected,
    nextCycleAt,
    onCancel,
}: {
    alertID: string
    snapshot: StatusSnapshot | null
    state: AlertState | null
    connected: boolean
    nextCycleAt: string | null
    onCancel: () => void
}) {
    const alert: SirenAlert | null = snapshot?.alert ?? null

    const [countdown, setCountdown] = useState<string | null>(null)
    const effectiveNextCycleAt = nextCycleAt ?? state?.nextCycleAt ?? null

    useEffect(() => {
        if (!effectiveNextCycleAt) { setCountdown(null); return }
        const tick = () => {
            const secs = Math.max(0, Math.round((new Date(effectiveNextCycleAt).getTime() - Date.now()) / 1000))
            if (secs <= 0) { setCountdown(null); return }
            const m = Math.floor(secs / 60)
            const s = secs % 60
            setCountdown(m > 0 ? `${m}m ${s}s` : `${s}s`)
        }
        tick()
        const interval = setInterval(tick, 1000)
        return () => clearInterval(interval)
    }, [effectiveNextCycleAt])

    const [downloading, setDownloading] = useState(false)
    const [showCancelConfirm, setShowCancelConfirm] = useState(false)
    const [cancelling, setCancelling] = useState(false)

    const cancelAlert = useCallback(async () => {
        setCancelling(true)
        try {
            await platformAPI.post(`api/siren/alerts/${alertID}/cancel`)
            setShowCancelConfirm(false)
            onCancel()
        } finally {
            setCancelling(false)
        }
    }, [alertID, onCancel])

    const downloadReport = useCallback(async () => {
        if (!alert) return
        setDownloading(true)
        try {
            const [recipientsRes, attemptsRes, creatorRes] = await Promise.all([
                platformAPI.get<RecipientExportRow[]>(`api/siren/alerts/${alertID}/recipients/export`),
                platformAPI.get<AttemptExportRow[]>(`api/siren/alerts/${alertID}/attempts/export`),
                alert.createdBy ? apiV2.get(`users/${alert.createdBy}`).catch(() => null) : Promise.resolve(null),
            ])
            const creatorName = creatorRes?.data?.profile
                ? `${creatorRes.data.profile.firstName} ${creatorRes.data.profile.lastName}`
                : ""

            const recipients = recipientsRes.data
            const attempts = attemptsRes.data
            const options = alert.responseOptions ?? []

            const normalise = (raw: string) => {
                const n = parseInt(raw, 10)
                if (!isNaN(n) && n >= 1 && n <= options.length) return options[n - 1]
                if (options.includes(raw)) return raw
                return raw
            }

            const currentStatus = state?.status ?? alert.status ?? "pending"
            const resolvedBC = alert.useDefaultBroadcastConfig ? state?.broadcastConfig : alert.broadcastConfig
            const resolvedDMs = alert.useDefaultDeliveryMethods
                ? (state?.alertConfig?.deliveryMethods ?? [])
                : (alert.deliveryMethods ?? [])

            const summaryRows: (string | number)[][] = [
                ["Alert Name", alert.name],
                ["Status", statusLabel(currentStatus)],
                ["Send At", moment(alert.sendAt).local().format("DD/MM/YYYY HH:mm")],
                ["Created By", creatorName],
                ...(alert.templateName ? [["Template", alert.templateName]] : []),
            ]
            if (state?.startedAt)   summaryRows.push(["Started",   moment(state.startedAt).local().format("DD/MM/YYYY HH:mm:ss")])
            if (state?.completedAt) summaryRows.push(["Completed", moment(state.completedAt).local().format("DD/MM/YYYY HH:mm:ss")])
            if (state?.cancelledAt) summaryRows.push(["Cancelled", moment(state.cancelledAt).local().format("DD/MM/YYYY HH:mm:ss")])
            if (state?.expiredAt)   summaryRows.push(["Expired",   moment(state.expiredAt).local().format("DD/MM/YYYY HH:mm:ss")])
            summaryRows.push(["Message", alert.content])
            if (alert.useSeparateSMSContent) summaryRows.push(["SMS Message", alert.smsContent])
            options.forEach((o, i) => summaryRows.push([i === 0 ? "Response Options" : "", `${i + 1}. ${o}`]))
            resolvedDMs.forEach((m, i) => summaryRows.push([i === 0 ? "Delivery Methods" : "", deliveryMethodLabel(m)]))
            if (resolvedBC) {
                summaryRows.push(
                    ["Broadcast Duration", formatSeconds(resolvedBC.broadcastDuration)],
                    ["Cycle Count", String(resolvedBC.cycleCount)],
                    ["Cycle Interval", formatSeconds(resolvedBC.cycleInterval)],
                    ["Method Interval", formatSeconds(resolvedBC.deliveryMethodInterval)],
                )
            }
            const summarySheet = XLSX.utils.aoa_to_sheet(summaryRows)
            summarySheet["!cols"] = [{wch: 22}, {wch: 60}]

            const responsesSheet = XLSX.utils.aoa_to_sheet([
                ["ID", "First Name", "Last Name", "Work Mobile Phone", "Personal Mobile Phone", "Home Phone", "Email Address", "Personal Email Address", "Response Number", "Response Text", "Responded At", "Responded", "Cycle", "Response Method", "Delivery Methods"],
                ...recipients.map(r => {
                    const rawVal = r.responseValue ?? null
                    const n = rawVal !== null ? parseInt(rawVal, 10) : NaN
                    const responseText = !isNaN(n) && n >= 1 && n <= options.length ? options[n - 1] : (rawVal ?? "")
                    return [
                        r.id,
                        r.firstName,
                        r.lastName,
                        r.workMobileE164,
                        r.personalMobileE164,
                        r.homePhoneE164,
                        r.email,
                        r.personalEmail,
                        rawVal ?? "",
                        responseText,
                        r.respondedAt ? moment(r.respondedAt).format("DD/MM/YYYY HH:mm:ss") : "",
                        r.respondedAt ? "Yes" : "No",
                        r.responseCycle ?? r.currentCycle ?? "",
                        r.responseVia ? formatResponseVia(r.responseVia) : "",
                        (r.deliveryMethods ?? []).join(", "),
                    ]
                }),
            ])
            responsesSheet["!cols"] = [
                {wch: 36}, {wch: 14}, {wch: 18}, {wch: 18}, {wch: 18}, {wch: 16},
                {wch: 28}, {wch: 28}, {wch: 14}, {wch: 28}, {wch: 20}, {wch: 10},
                {wch: 8},  {wch: 16}, {wch: 30},
            ]

            const deliverySheet = XLSX.utils.aoa_to_sheet([
                ["ID", "Recipient ID", "First Name", "Last Name", "Cycle", "Delivery Method", "From", "To", "Status", "Skip Reason", "Sent At", "Failed At"],
                ...attempts.map(a => [
                    a.id,
                    a.recipientID,
                    a.firstName,
                    a.lastName,
                    a.cycle,
                    a.deliveryMethod,
                    a.from,
                    a.to,
                    a.status,
                    formatSkipReason(a.skipReason, a.deliveryMethod),
                    a.sentAt ? moment(a.sentAt).format("DD/MM/YYYY HH:mm:ss") : "",
                    a.failedAt ? moment(a.failedAt).format("DD/MM/YYYY HH:mm:ss") : "",
                ]),
            ])
            deliverySheet["!cols"] = [
                {wch: 36}, {wch: 36}, {wch: 14}, {wch: 18}, {wch: 8},  {wch: 18},
                {wch: 20}, {wch: 20}, {wch: 14}, {wch: 28}, {wch: 20}, {wch: 20},
            ]

            const wb = XLSX.utils.book_new()
            XLSX.utils.book_append_sheet(wb, summarySheet, "Summary")
            if (options.length > 0) XLSX.utils.book_append_sheet(wb, responsesSheet, "Responses")
            XLSX.utils.book_append_sheet(wb, deliverySheet, "Delivery")

            const timestamp = new Date().toISOString().replace(/:/g, "-").split(".")[0]
            XLSX.writeFile(wb, `alert_report_${timestamp}.xlsx`)
        } finally {
            setDownloading(false)
        }
    }, [alertID, alert, state])

    if (!alert) return null

    const currentStatus = state?.status ?? alert.status ?? "pending"
    const statusColor = STATUS_COLORS[currentStatus] ?? "#B2B1B1"
    const resolvedBC = alert.useDefaultBroadcastConfig ? state?.broadcastConfig : alert.broadcastConfig
    const resolvedDMs = alert.useDefaultDeliveryMethods
        ? (state?.alertConfig?.deliveryMethods ?? [])
        : (alert.deliveryMethods ?? [])

    return (
        <>
            <Card
                title={alert.name}
                headerActions={
                    <span className="flex items-center gap-1.5 text-xs text-grey">
                        <span className={clsx("w-2 h-2 rounded-full inline-block", connected ? "bg-green animate-pulse" : "bg-red")}/>
                        {connected ? "Connected" : "Disconnected"}
                    </span>
                }
            >
                <dl className="grid grid-cols-2 gap-x-4 gap-y-3">
                    <InfoRow label="Status" value={
                        <span className="font-medium" style={{color: statusColor}}>{statusLabel(currentStatus)}</span>
                    }/>
                    <InfoRow label="Send At" value={moment(alert.sendAt).local().format("DD/MM/YYYY HH:mm")}/>
                    {alert.createdBy && <InfoRow label="Created By" value={<UserName id={alert.createdBy}/>}/>}
                    {alert.templateName && <InfoRow label="Template" value={alert.templateName}/>}
                    {state && (
                        <>
                            {(currentStatus === "pending" || currentStatus === "in_progress") && (
                                <InfoRow label="Cycle" value={
                                    <span className="flex items-center gap-2">
                                        {(() => {
                                            const bc = alert.useDefaultBroadcastConfig ? state?.broadcastConfig : alert.broadcastConfig
                                            const cycleStr = `Cycle ${state.currentCycle}${bc ? ` / ${bc.cycleCount}` : ""}`
                                            return countdown
                                                ? <span className="text-xs text-grey">{cycleStr} starts in {countdown}</span>
                                                : <span>{cycleStr}</span>
                                        })()}
                                    </span>
                                }/>
                            )}
                            {state.startedAt && <InfoRow label="Started" value={moment(state.startedAt).local().format("DD/MM/YYYY HH:mm:ss")}/>}
                            {state.completedAt && <InfoRow label="Completed" value={moment(state.completedAt).local().format("DD/MM/YYYY HH:mm:ss")}/>}
                            {state.cancelledAt && <InfoRow label="Cancelled" value={moment(state.cancelledAt).local().format("DD/MM/YYYY HH:mm:ss")}/>}
                            {state.expiredAt && <InfoRow label="Expired" value={moment(state.expiredAt).local().format("DD/MM/YYYY HH:mm:ss")}/>}
                        </>
                    )}
                    <InfoRow label="Message" value={<span className="break-words whitespace-pre-wrap">{alert.content}</span>}/>
                    {alert.useSeparateSMSContent && (
                        <InfoRow label="SMS Message" value={<span className="break-words whitespace-pre-wrap">{alert.smsContent}</span>}/>
                    )}
                </dl>

                {(resolvedBC || resolvedDMs.length > 0 || alert.useDefaultBroadcastConfig) && (
                    <>
                        <hr className="opacity-20"/>
                        <h3 className="text-sm font-semibold text-grey">
                            Broadcast Config
                            {(alert.useDefaultBroadcastConfig || alert.useDefaultDeliveryMethods) && <span className="ml-2 font-normal text-grey opacity-60">(default)</span>}
                        </h3>
                        {resolvedBC && (
                            <dl className="grid grid-cols-2 gap-x-4 gap-y-3">
                                <InfoRow label="Broadcast Duration" value={formatSeconds(resolvedBC.broadcastDuration)}/>
                                <InfoRow label="Cycle Interval" value={formatSeconds(resolvedBC.cycleInterval)}/>
                                <InfoRow label="Cycle Count" value={resolvedBC.cycleCount}/>
                            </dl>
                        )}
                        {(resolvedDMs.length > 0 || resolvedBC) && (
                            <>
                                <hr className="opacity-20"/>
                                <h3 className="text-sm font-semibold text-grey">Delivery</h3>
                                <dl className="grid grid-cols-2 gap-x-4 gap-y-3">
                                    {resolvedDMs.length > 0 && (
                                        <InfoRow label="Delivery Methods" value={
                                            <div className="flex flex-col gap-0.5">
                                                {resolvedDMs.map(m => (
                                                    <span key={m}>{deliveryMethodLabel(m)}</span>
                                                ))}
                                            </div>
                                        }/>
                                    )}
                                    {resolvedBC && (
                                        <InfoRow label="Method Interval" value={formatSeconds(resolvedBC.deliveryMethodInterval)}/>
                                    )}
                                    {alert.smsSenderID && resolvedDMs.some(m => m.startsWith("sms_")) && (
                                        <InfoRow label="Preferred SMS Sender ID" value={
                                            alert.smsSenderID === "alphanumeric" ? "Alphanumeric" :
                                            alert.smsSenderID === "long_code" ? "Long Code" : "No Preference"
                                        }/>
                                    )}
                                </dl>
                            </>
                        )}
                    </>
                )}

                <div className="flex gap-2 mt-2">
                    <button
                        onClick={downloadReport}
                        disabled={downloading}
                        className="px-3 py-1.5 rounded border border-light-grey text-xs font-medium text-grey hover:bg-ice transition-colors disabled:opacity-60"
                    >
                        {downloading ? "Downloading…" : "Download Report"}
                    </button>
                    {(currentStatus === "pending" || currentStatus === "in_progress") && (
                        <button
                            onClick={() => setShowCancelConfirm(true)}
                            className="px-3 py-1.5 rounded border border-mid-red text-xs font-medium text-red hover:bg-desaturated-red transition-colors"
                        >
                            Cancel Alert
                        </button>
                    )}
                </div>
            </Card>

            {showCancelConfirm && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
                    <div className="bg-white rounded-lg shadow-xl p-6 max-w-sm w-full mx-4 flex flex-col gap-4">
                        <h2 className="text-base font-semibold text-dark-dark-grey">Cancel Alert</h2>
                        <p className="text-sm text-grey">Are you sure you want to cancel this alert? Any in-progress sends will complete but no further cycles or delivery methods will be attempted.</p>
                        <div className="flex gap-3 justify-end">
                            <button
                                onClick={() => setShowCancelConfirm(false)}
                                className="px-4 py-2 rounded border border-light-grey text-sm text-grey"
                            >
                                Go Back
                            </button>
                            <button
                                onClick={cancelAlert}
                                disabled={cancelling}
                                className="px-4 py-2 rounded bg-red text-white text-sm font-medium disabled:opacity-60"
                            >
                                {cancelling ? "Cancelling…" : "Cancel Alert"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    )
}
